import React from "react";
import { useTheme } from "styled-components";
import { Button } from "@hangar/web-components/core/Button";
import { IconButton } from "@hangar/web-components/core/IconButton";
import { Text } from "@hangar/web-components/core/Text";
import { Spinner } from "@hangar/web-components/core/Spinner";
import { Skeleton } from "@hangar/web-components/core/Skeleton";
import { CommunicationStar } from "@hangar/react-icons/core/latam/Communication/CommunicationStar";
import { SecurityProtection } from "@hangar/react-icons/core/latam/Security/SecurityProtection";
import { HumanUser } from "@hangar/react-icons/core/latam/Human/HumanUser";
import { FlightTakeOff } from "@hangar/react-icons/core/latam/Flight/FlightTakeOff";
import { FlightLanding } from "@hangar/react-icons/core/latam/Flight/FlightLanding";
import { FlightAirport } from "@hangar/react-icons/core/latam/Flight/FlightAirport";
import { FlightConnection } from "@hangar/react-icons/core/latam/Flight/FlightConnection";
import { LoyaltyRewards } from "@hangar/react-icons/core/latam/Loyalty/LoyaltyRewards";
import { LoyaltyGift } from "@hangar/react-icons/core/latam/Loyalty/LoyaltyGift";
import { LoyaltyCreditCard } from "@hangar/react-icons/core/latam/Loyalty/LoyaltyCreditCard";
import { LoyaltyAccumulate } from "@hangar/react-icons/core/latam/Loyalty/LoyaltyAccumulate";
import { DestinationWorld } from "@hangar/react-icons/core/latam/Destination/DestinationWorld";
import { PaymentsMoney } from "@hangar/react-icons/core/latam/Loyalty/PaymentsMoney";
import { OperatorLA } from "@hangar/react-icons/core/operator/OperatorLA";
import { OperatorAA } from "@hangar/react-icons/core/operator/OperatorAA";
import { OperatorIB } from "@hangar/react-icons/core/operator/OperatorIB";
import { SparklesFilled } from "@hangar/react-icons/core/interaction/SparklesFilled";
import { ArrowCircleRightFilled } from "@hangar/react-icons/core/interaction/ArrowCircleRightFilled";
import { SecurityPadlock } from "@hangar/react-icons/core/latam/Security/SecurityPadlock";
import { SecurityEmergency } from "@hangar/react-icons/core/latam/Security/SecurityEmergency";
import { CommunicationEmail } from "@hangar/react-icons/core/latam/Communication/CommunicationEmail";
import { CommunicationWifi } from "@hangar/react-icons/core/latam/Communication/CommunicationWifi";
import { DevicePhone } from "@hangar/react-icons/core/latam/Device/DevicePhone";
import { DeviceQRCode } from "@hangar/react-icons/core/latam/Device/DeviceQRCode";
import { HealthMask } from "@hangar/react-icons/core/latam/Health/HealthMask";
import { HealthVaccine } from "@hangar/react-icons/core/latam/Health/HealthVaccine";
import { Breadcrumb } from "../Breadcrumb/Breadcrumb";
import {
  Container,
  Section,
  ComponentContainer,
  ComponentVariantContainer,
  ComponentTextContainer,
  ComponentGrid,
  ComponentCard,
  ComponentTitle,
  ComponentDescription,
  ComponentDemo,
  SkeletonContainer,
  ColorGrid,
  ColorCategory,
  ColorCategoryTitle,
  ColorSwatch,
  ColorBox,
  ColorInfo,
  ColorName,
  ColorValue,
  IconContainer,
  StyledLink,
} from "./ThemeDemo.styles";

export const ThemeDemo: React.FC = () => {
  const theme = useTheme();
  return (
    <>
      <Breadcrumb
        title="LATAM Design System Components"
        description={
          <>
            Explore our component library with interactive examples including buttons, icons, spinners, skeletons, and all the theme definitions. For complete documentation and advanced usage examples, visit our{" "}
            <StyledLink
              href="https://hangar-web-929bf2.gitlab.io/"
              target="_blank"
              rel="noopener noreferrer"
            >
              Storybook Documentation
            </StyledLink>
          </>
        }
        items={[
          { label: "Theme" }
        ]}
      />
      
      <Container>
        <ComponentGrid>
          <ComponentCard>
            <ComponentTitle>Buttons</ComponentTitle>
            <ComponentDescription>
              Primary, secondary, and tertiary button variants in different sizes and states
            </ComponentDescription>
            <ComponentDemo>
              <ComponentVariantContainer>
                <Button
                  colorMode="standard"
                  variant="primary"
                  label="Primary"
                />
                <Button
                  colorMode="standard"
                  variant="secondary"
                  label="Secondary"
                />
                <Button
                  colorMode="standard"
                  variant="tertiary"
                  label="Tertiary"
                />
                <Button
                  colorMode="standard"
                  variant="text"
                  label="Text"
                />
              </ComponentVariantContainer>
              <ComponentVariantContainer>
                <Button
                  colorMode="standard"
                  variant="primary"
                  size="compact"
                  label="Compact"
                />
                <Button
                  colorMode="standard"
                  variant="secondary"
                  size="compact"
                  label="Compact"
                />
              </ComponentVariantContainer>
              <ComponentVariantContainer>
                <Button
                  colorMode="standard"
                  variant="primary"
                  label="With Start Icon"
                  startIcon={CommunicationStar as any}
                />
                <Button
                  colorMode="standard"
                  variant="secondary"
                  label="With End Icon"
                  endIcon={ArrowCircleRightFilled as any}
                />
              </ComponentVariantContainer>
              <ComponentVariantContainer>
                <Button
                  colorMode="standard"
                  variant="primary"
                  label="Disabled"
                  disabled
                />
                <Button
                  colorMode="standard"
                  variant="primary"
                  label="Loading"
                  loading
                />
              </ComponentVariantContainer>
            </ComponentDemo>
          </ComponentCard>

          <ComponentCard>
            <ComponentTitle>Icon Buttons</ComponentTitle>
            <ComponentDescription>
              Icon-only buttons in different variants and sizes
            </ComponentDescription>
            <ComponentDemo>
              <ComponentVariantContainer>
                <IconButton
                  variant="primary"
                  icon={CommunicationStar}
                />
                <IconButton
                  variant="secondary"
                  icon={SecurityProtection}
                />
                <IconButton
                  variant="tertiary"
                  icon={HumanUser}
                />
              </ComponentVariantContainer>
              <ComponentVariantContainer>
                <IconButton
                  variant="primary"
                  size="compact"
                  icon={CommunicationStar}
                />
                <IconButton
                  variant="secondary"
                  size="compact"
                  icon={SecurityProtection}
                />
              </ComponentVariantContainer>
              <ComponentVariantContainer>
                <IconButton
                  variant="primary"
                  icon={HumanUser}
                  disabled
                />
                <IconButton
                  variant="primary"
                  icon={SecurityProtection}
                  loading
                />
              </ComponentVariantContainer>
            </ComponentDemo>
          </ComponentCard>

          <ComponentCard>
            <ComponentTitle>Icons</ComponentTitle>
            <ComponentDescription>
              LATAM design system icons for different use cases and categories
            </ComponentDescription>
            <ComponentDemo>
              <ComponentVariantContainer>
                <IconContainer>
                  <CommunicationStar size={24} />
                  <Text variant="label-xs">Star</Text>
                </IconContainer>
                <IconContainer>
                  <CommunicationEmail size={24} />
                  <Text variant="label-xs">Email</Text>
                </IconContainer>
                <IconContainer>
                  <SecurityProtection size={24} />
                  <Text variant="label-xs">Protection</Text>
                </IconContainer>
                <IconContainer>
                  <SecurityPadlock size={24} />
                  <Text variant="label-xs">Padlock</Text>
                </IconContainer>
                <IconContainer>
                  <SecurityEmergency size={24} />
                  <Text variant="label-xs">Emergency</Text>
                </IconContainer>
                <IconContainer>
                  <HumanUser size={24} />
                  <Text variant="label-xs">User</Text>
                </IconContainer>
              </ComponentVariantContainer>
              <ComponentVariantContainer>
                <IconContainer>
                  <FlightTakeOff size={24} />
                  <Text variant="label-xs">Take Off</Text>
                </IconContainer>
                <IconContainer>
                  <FlightLanding size={24} />
                  <Text variant="label-xs">Landing</Text>
                </IconContainer>
                <IconContainer>
                  <FlightAirport size={24} />
                  <Text variant="label-xs">Airport</Text>
                </IconContainer>
                <IconContainer>
                  <FlightConnection size={24} />
                  <Text variant="label-xs">Connection</Text>
                </IconContainer>
                <IconContainer>
                  <LoyaltyRewards size={24} />
                  <Text variant="label-xs">Rewards</Text>
                </IconContainer>
                <IconContainer>
                  <LoyaltyGift size={24} />
                  <Text variant="label-xs">Gift</Text>
                </IconContainer>
              </ComponentVariantContainer>
              <ComponentVariantContainer>
                <IconContainer>
                  <LoyaltyCreditCard size={24} />
                  <Text variant="label-xs">Credit Card</Text>
                </IconContainer>
                <IconContainer>
                  <LoyaltyAccumulate size={24} />
                  <Text variant="label-xs">Accumulate</Text>
                </IconContainer>
                <IconContainer>
                  <DestinationWorld size={24} />
                  <Text variant="label-xs">World</Text>
                </IconContainer>
                <IconContainer>
                  <PaymentsMoney size={24} />
                  <Text variant="label-xs">Money</Text>
                </IconContainer>
                <IconContainer>
                  <DevicePhone size={24} />
                  <Text variant="label-xs">Phone</Text>
                </IconContainer>
                <IconContainer>
                  <CommunicationWifi size={24} />
                  <Text variant="label-xs">WiFi</Text>
                </IconContainer>
                <IconContainer>
                  <DeviceQRCode size={24} />
                  <Text variant="label-xs">QR Code</Text>
                </IconContainer>
              </ComponentVariantContainer>
              <ComponentVariantContainer>
                <IconContainer>
                  <HealthMask size={24} />
                  <Text variant="label-xs">Mask</Text>
                </IconContainer>
                <IconContainer>
                  <HealthVaccine size={24} />
                  <Text variant="label-xs">Vaccine</Text>
                </IconContainer>
                <IconContainer>
                  <OperatorLA size={24} />
                  <Text variant="label-xs">LATAM</Text>
                </IconContainer>
                <IconContainer>
                  <OperatorAA size={24} />
                  <Text variant="label-xs">American</Text>
                </IconContainer>
                <IconContainer>
                  <OperatorIB size={24} />
                  <Text variant="label-xs">Iberia</Text>
                </IconContainer>
                <IconContainer>
                  <SparklesFilled size={24} />
                  <Text variant="label-xs">Sparkles</Text>
                </IconContainer>
                <IconContainer>
                  <ArrowCircleRightFilled size={24} />
                  <Text variant="label-xs">Arrow</Text>
                </IconContainer>
              </ComponentVariantContainer>
            </ComponentDemo>
          </ComponentCard>

          <ComponentCard>
            <ComponentTitle>Links</ComponentTitle>
            <ComponentDescription>
              Text links in different sizes, color modes and icon configurations
            </ComponentDescription>
            <ComponentDemo>
              <ComponentVariantContainer>
                <StyledLink href="#" $size="sm">Small Link</StyledLink>
                <StyledLink href="#" $size="md">Medium Link</StyledLink>
                <StyledLink href="#" $size="lg">Large Link</StyledLink>
              </ComponentVariantContainer>
              <ComponentVariantContainer>
                <StyledLink href="#" $size="md">Link without icon</StyledLink>
                <StyledLink href="#" $size="md" $inverse style={{ backgroundColor: '#050847', padding: '0.5rem', borderRadius: '0.25rem' }}>
                  Inverse Link
                </StyledLink>
              </ComponentVariantContainer>
            </ComponentDemo>
          </ComponentCard>

        </ComponentGrid>

        <ComponentGrid>
          <ComponentCard>
            <ComponentTitle>Spinners</ComponentTitle>
            <ComponentDescription>
              Loading indicators in different sizes and colors
            </ComponentDescription>
            <ComponentDemo>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                <div style={{ padding: '1rem', display: 'flex', alignItems: 'center', gap: '1rem', backgroundColor: 'rgba(0,0,0,0.05)', borderRadius: '0.5rem' }}>
                  <Spinner size="compact" />
                  <Text variant="label-sm">Compact Spinner</Text>
                </div>
                <div style={{ padding: '1rem', display: 'flex', alignItems: 'center', gap: '1rem', backgroundColor: 'rgba(0,0,0,0.05)', borderRadius: '0.5rem' }}>
                  <Spinner size="normal" />
                  <Text variant="label-sm">Normal Spinner</Text>
                </div>
                <div style={{ padding: '1rem', display: 'flex', alignItems: 'center', gap: '1rem', backgroundColor: 'rgba(0,0,0,0.05)', borderRadius: '0.5rem' }}>
                  <Spinner colorMode="inverse" size="normal" />
                  <Text variant="label-sm">Inverse Spinner</Text>
                </div>
              </div>
            </ComponentDemo>
          </ComponentCard>

          <ComponentCard>
            <ComponentTitle>Skeletons</ComponentTitle>
            <ComponentDescription>
              Different skeleton variations for content placeholders
            </ComponentDescription>
            <ComponentDemo>
              <SkeletonContainer>
                <Skeleton />
                <Skeleton />
                <Skeleton />
                <Skeleton />
              </SkeletonContainer>
            </ComponentDemo>
          </ComponentCard>
        </ComponentGrid>



        <Section>
          <Text variant="heading-md">Color System</Text>
          <Text variant="body-md">
            LATAM Design System color palette with primary, secondary, and semantic colors
          </Text>
          <ColorGrid>
            <ColorCategory>
              <ColorCategoryTitle>Background Colors</ColorCategoryTitle>
              <ColorSwatch color={theme.tokens.color.background.primary}>
                <ColorBox color={theme.tokens.color.background.primary} />
                <ColorInfo>
                  <ColorName>Primary</ColorName>
                  <ColorValue>{theme.tokens.color.background.primary}</ColorValue>
                </ColorInfo>
              </ColorSwatch>
              <ColorSwatch color={theme.tokens.color.background.secondary}>
                <ColorBox color={theme.tokens.color.background.secondary} />
                <ColorInfo>
                  <ColorName>Secondary</ColorName>
                  <ColorValue>{theme.tokens.color.background.secondary}</ColorValue>
                </ColorInfo>
              </ColorSwatch>

            </ColorCategory>

            <ColorCategory>
              <ColorCategoryTitle>Text Colors</ColorCategoryTitle>
              <ColorSwatch color={theme.tokens.color.text.primary}>
                <ColorBox color={theme.tokens.color.text.primary} />
                <ColorInfo>
                  <ColorName>Primary</ColorName>
                  <ColorValue>{theme.tokens.color.text.primary}</ColorValue>
                </ColorInfo>
              </ColorSwatch>
              <ColorSwatch color={theme.tokens.color.text.secondary}>
                <ColorBox color={theme.tokens.color.text.secondary} />
                <ColorInfo>
                  <ColorName>Secondary</ColorName>
                  <ColorValue>{theme.tokens.color.text.secondary}</ColorValue>
                </ColorInfo>
              </ColorSwatch>

            </ColorCategory>

            <ColorCategory>
              <ColorCategoryTitle>Border Colors</ColorCategoryTitle>
              <ColorSwatch color={theme.tokens.color.border.primary}>
                <ColorBox color={theme.tokens.color.border.primary} />
                <ColorInfo>
                  <ColorName>Primary</ColorName>
                  <ColorValue>{theme.tokens.color.border.primary}</ColorValue>
                </ColorInfo>
              </ColorSwatch>

              <ColorSwatch color={theme.tokens.color.border.focusStandard}>
                <ColorBox color={theme.tokens.color.border.focusStandard} />
                <ColorInfo>
                  <ColorName>Focus Standard</ColorName>
                  <ColorValue>{theme.tokens.color.border.focusStandard}</ColorValue>
                </ColorInfo>
              </ColorSwatch>
            </ColorCategory>

            <ColorCategory>
              <ColorCategoryTitle>Interaction Colors</ColorCategoryTitle>
              <ColorSwatch color={theme.tokens.color.interaction.mediumDefault}>
                <ColorBox color={theme.tokens.color.interaction.mediumDefault} />
                <ColorInfo>
                  <ColorName>Medium Default</ColorName>
                  <ColorValue>{theme.tokens.color.interaction.mediumDefault}</ColorValue>
                </ColorInfo>
              </ColorSwatch>
              <ColorSwatch color={theme.tokens.color.interaction.strongDefault}>
                <ColorBox color={theme.tokens.color.interaction.strongDefault} />
                <ColorInfo>
                  <ColorName>Strong Default</ColorName>
                  <ColorValue>{theme.tokens.color.interaction.strongDefault}</ColorValue>
                </ColorInfo>
              </ColorSwatch>
              <ColorSwatch color={theme.tokens.color.interaction.softDefault}>
                <ColorBox color={theme.tokens.color.interaction.softDefault} />
                <ColorInfo>
                  <ColorName>Soft Default</ColorName>
                  <ColorValue>{theme.tokens.color.interaction.softDefault}</ColorValue>
                </ColorInfo>
              </ColorSwatch>
            </ColorCategory>

            <ColorCategory>
              <ColorCategoryTitle>Icon Colors</ColorCategoryTitle>
              <ColorSwatch color={theme.tokens.color.icon.primary}>
                <ColorBox color={theme.tokens.color.icon.primary} />
                <ColorInfo>
                  <ColorName>Primary</ColorName>
                  <ColorValue>{theme.tokens.color.icon.primary}</ColorValue>
                </ColorInfo>
              </ColorSwatch>


            </ColorCategory>

            <ColorCategory>
              <ColorCategoryTitle>Surface Colors</ColorCategoryTitle>
              <ColorSwatch color={theme.tokens.color.surface.primary}>
                <ColorBox color={theme.tokens.color.surface.primary} />
                <ColorInfo>
                  <ColorName>Primary</ColorName>
                  <ColorValue>{theme.tokens.color.surface.primary}</ColorValue>
                </ColorInfo>
              </ColorSwatch>
              <ColorSwatch color={theme.tokens.color.surface.secondary}>
                <ColorBox color={theme.tokens.color.surface.secondary} />
                <ColorInfo>
                  <ColorName>Secondary</ColorName>
                  <ColorValue>{theme.tokens.color.surface.secondary}</ColorValue>
                </ColorInfo>
              </ColorSwatch>
              <ColorSwatch color={theme.tokens.color.surface.inverse}>
                <ColorBox color={theme.tokens.color.surface.inverse} />
                <ColorInfo>
                  <ColorName>Inverse</ColorName>
                  <ColorValue>{theme.tokens.color.surface.inverse}</ColorValue>
                </ColorInfo>
              </ColorSwatch>
              <ColorSwatch color={theme.tokens.color.surface.errorSoft}>
                <ColorBox color={theme.tokens.color.surface.errorSoft} />
                <ColorInfo>
                  <ColorName>Error Soft</ColorName>
                  <ColorValue>{theme.tokens.color.surface.errorSoft}</ColorValue>
                </ColorInfo>
              </ColorSwatch>
            </ColorCategory>
          </ColorGrid>
        </Section>

        <Section>
          <Text variant="heading-md">Typography System</Text>
          <Text variant="body-md">
            Complete text hierarchy with display, heading, body, and label variants
          </Text>
          <ComponentTextContainer>
            <Text variant="display-lg">Display Large</Text>
            <Text variant="display-md">Display Medium</Text>
            <Text variant="heading-xl">Heading Extra Large</Text>
            <Text variant="heading-lg">Heading Large</Text>
            <Text variant="heading-md">Heading Medium</Text>
            <Text variant="heading-sm">Heading Small</Text>
            <Text variant="body-lg">Body Large</Text>
            <Text variant="body-md">Body Medium</Text>
            <Text variant="body-sm">Body Small</Text>
            <Text variant="label-lg">Label Large</Text>
            <Text variant="label-md">Label Medium</Text>
            <Text variant="label-sm">Label Small</Text>
            <Text variant="label-xs">Label Extra Small</Text>
          </ComponentTextContainer>
        </Section>

        <Section>
          <Text variant="heading-md">Text Colors & Styles</Text>
          <Text variant="body-md">
            Text component with different colors, bold and italic variations
          </Text>
          <ComponentTextContainer>
            <Text variant="body-md" color="primary">Primary Text Color</Text>
            <Text variant="body-md" color="secondary">Secondary Text Color</Text>
            <Text variant="body-md" color="tertiary">Tertiary Text Color</Text>
            <Text variant="body-md" color="error">Error Text Color</Text>
            <Text variant="body-md" color="disabled">Disabled Text Color</Text>
            <div style={{ backgroundColor: '#050847', padding: '0.5rem', borderRadius: '0.25rem', marginTop: '0.5rem' }}>
              <Text variant="body-md" color="inverse">Inverse Text Color</Text>
            </div>
            <Text variant="body-md" bold>Bold Text</Text>
            <Text variant="body-md" italic>Italic Text</Text>
            <Text variant="body-md" bold italic>Bold Italic Text</Text>
          </ComponentTextContainer>
        </Section>

        <Section>
          <Text variant="heading-md">Spacing System</Text>
          <Text variant="body-md">
            Design tokens for consistent spacing throughout the application
          </Text>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            {Object.entries(theme.tokens.padding).map(([key, value]) => (
              <div key={key} style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                <div style={{ 
                  width: `${value}px`, 
                  height: '24px', 
                  backgroundColor: theme.tokens.color.interaction.mediumDefault,
                  borderRadius: '2px'
                }} />
                <Text variant="body-sm">{key}: {value}px</Text>
              </div>
            ))}
          </div>
        </Section>

        <Section>
          <Text variant="heading-md">Border Radius</Text>
          <Text variant="body-md">
            Border radius tokens for consistent rounded corners
          </Text>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: '1rem' }}>
            {Object.entries(theme.tokens.border.radius).map(([key, value]) => (
              <div key={key} style={{ textAlign: 'center' }}>
                <div style={{ 
                  width: '60px', 
                  height: '60px', 
                  backgroundColor: theme.tokens.color.interaction.softDefault,
                  borderRadius: `${value}px`,
                  margin: '0 auto 0.5rem'
                }} />
                <Text variant="label-xs">{key}</Text>
                <Text variant="label-xs" color="secondary">{value}px</Text>
              </div>
            ))}
          </div>
        </Section>
      </Container>
    </>
  );
};

export default ThemeDemo;
